<template>
  <wrap />
</template>

<script>
import Wrap from '@/components/views/index';
import { computed, getCurrentInstance, onBeforeMount, onBeforeUnmount } from 'vue';
import LocaleManager from '@/common/utils/LocaleManager';
import DynamicLinkManager from '@/common/utils/DynamicLinkManager';
import { useRouter } from 'vue-router';
import DeviceManager from '@/common/utils/DeviceManager';
import { modalList, show } from '@/common/utils/ControlModal';
import Util from '@/common/utils/Util';
import store from '@/store';
import SplashIntroManager from '@/common/utils/SplashIntroManager';
import { resyncChatForPush } from '@/common/utils/pushResync';

const INTRO_HIDE_FALLBACK_DELAY = 4000;
const APP_INTRO_FALLBACK_KEY = 'app-intro';

export default {
  name: 'App',
  components: { Wrap },
  setup() {
    const { proxy } = getCurrentInstance();
    const { replace, push } = useRouter();

    onBeforeMount(async () => {
      scheduleIntroHideFallback();
      // root.vue 보다 먼저 실행됨
      let user = store.getters['user/getUserData'];
      if (user?.userNo && Util.isApp()) await Util.nativeFetchIsOnAlert('isOnAlert');
      LocaleManager.initialize(setLocale);
      await DeviceManager.initialize(deviceReducer);
      await DynamicLinkManager.initialize(routeTo);
      await DynamicLinkManager.initializePush({
        isAuthenticated: () => {
          const accessToken = store.getters['user/getAccessToken'];
          const { userNo } = store.getters['user/getUserData'] ?? {};
          return Boolean(accessToken && userNo);
        },
        getCurrentPath: () => window.location.pathname,
        getChatRoomState: () => store.state.chat,
        navigate: (path) => push(path),
        resync: (target) => resyncChatForPush(store, target)
      });
      void initializeConstants();
    });

    onBeforeUnmount(() => {
      SplashIntroManager.clearFallback(APP_INTRO_FALLBACK_KEY);
    });

    const updateInfo = computed(() => store.getters['user/getUpdateInfo']);

    const setLocale = (locale) => {
      if (!locale) return;
      store.dispatch('locale/setLocale', locale);
      store.commit('user/setUserLocale', locale);
      proxy.$i18n.locale = locale.toLowerCase();
    };

    const routeTo = (link) => {
      // link = store.getters['dynamiclink/getPath'];
      console.warn('routeTo 실행', link, !link);
      console.log('pathname :: ', window.location.pathname);
      if (!link) return;

      // 현재 채팅방에 있는 상태에서 다른 채팅 푸시를 받았을 시 메인 보낸 후 라우팅
      if (/\/room\/.*\/.*/gi.test(window.location.pathname)) {
        replace('/main').then(() => {
          console.log('현재 채팅방에서 푸시 수신, 대기 후 라우팅 :: ', link);
          setTimeout(() => {
            push(link);
          }, 1000);
        });
      } else {
        console.warn('routeTo push() :: ', link);
        setTimeout(() => {
          push(link);
        }, 100);
      }
    };

    const deviceReducer = (action = DeviceManager.actions.REDIRECT) => {
      console.warn('deviceReducer :: ', action);
      switch (action) {
        case DeviceManager.actions.FIRST_RUN:
          // 권한 요청 모달을 띄움
          console.warn('FIRST_RUN');
          firstRun();
          hideIntro();
          break;
        case DeviceManager.actions.UPDATE:
          // 업데이트 모달을 띄움
          console.warn('UPDATE');
          update();
          hideIntro();
          break;
        case DeviceManager.actions.REDIRECT: {
          // console.warn('REDIRECT :: ', window.path);
          // store.dispatch('user/SYNC_FCM_KEY');
          SplashIntroManager.clearFallback(APP_INTRO_FALLBACK_KEY);
          const { latitude, longitude } = store.getters['user/getUserPosition'];
          if (Number(latitude ?? 0) === 0 || Number(longitude ?? 0) === 0) {
            store.dispatch('user/GET_CURRENT_POSITION');
          }
          replace({ path: '/main', query: { isRoot: 1 } });
          break;
        }
      }
    };

    const firstRun = () => {
      show(modalList.Permission, {}, DeviceManager.actions.FIRST_RUN);
    };

    const hideIntro = () => {
      SplashIntroManager.hideIntro(APP_INTRO_FALLBACK_KEY);
    };

    const shouldShowConstantConnectionModal = () => {
      const initializeState = store.getters['constant/getInitializeState'];
      return (
        Util.isApp() &&
        !store.getters['constant/hasConstantCache'] &&
        !initializeState?.isLoading &&
        initializeState?.isInitialized === false
      );
    };

    const requestConstantConnectionModal = (result = {}) => {
      SplashIntroManager.requestReloadModal(
        {
          reason: 'CONSTANT_INITIALIZE_FAILED',
          force: true,
          getContext: () => ({
            status: result.status ?? '',
            errorMessage: result.errorMessage ?? '',
            hasCache: store.getters['constant/hasConstantCache'],
            initializeState: store.getters['constant/getInitializeState'],
            currentPath: window.location.pathname
          })
        },
        shouldShowConstantConnectionModal
      );
    };

    const initializeConstants = async () => {
      try {
        const result = await store.dispatch('constant/initialize', { startup: true });
        if (result?.shouldShowConnectionModal) requestConstantConnectionModal(result);
      } catch (error) {
        requestConstantConnectionModal({
          status: 0,
          errorMessage: error?.message ?? ''
        });
      }
    };

    const scheduleIntroHideFallback = () => {
      SplashIntroManager.scheduleFallback(APP_INTRO_FALLBACK_KEY, {
        delayMs: INTRO_HIDE_FALLBACK_DELAY,
        reason: 'APP_INTRO_HIDE_FALLBACK_TIMEOUT',
        canRun: () => window.location.pathname === '/',
        getContext: () => ({
          delayMs: INTRO_HIDE_FALLBACK_DELAY,
          currentPath: window.location.pathname
        })
      });
    };

    const update = () => {
      const { hasUpdate, isModal } = updateInfo.value;
      if (!hasUpdate) {
        DeviceManager.reduce();
      } else {
        if (isModal) show(modalList.Update, {}, DeviceManager.actions.UPDATE);
        else DeviceManager.reduce();
      }
    };

    return {};
  }
};
</script>

<style scoped></style>
